import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { SketchPicker } from 'react-color';
import { Translate } from '../../../../../../../shared/LanguageService';
import SuccessSnackbar from '../../../../../../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../../../../../../shared/feedback/FailureSnackbar';
import { usePinPosition } from '../state/use-pin-position';
import {
    DEFAULT_PIN_COLOR,
    DEFAULT_PIN_SIZE_PERCENT,
    MAX_PIN_SIZE_PERCENT,
    MIN_PIN_SIZE_PERCENT,
    PIN_SIZE_STEP_PERCENT,
    PRESET_PIN_COLORS
} from '../state/utils';
import {
    PinRequest,
    useCreatePinnedImageMutation
} from '../queries/use-mutation-create-pinned-image';
import { PinImagePreview } from './PinImagePreview';

type PlacePinDialogProps = {
    open: boolean;
    onClose: () => void;
    src: string;
    alt: string;
    isLoading: boolean;
    onCreate: (pin: PinRequest) => void;
};

export function PlacePinDialog({
    open,
    onClose,
    src,
    alt,
    isLoading,
    onCreate
}: PlacePinDialogProps) {
    const [sizePercent, setSizePercent] = React.useState(DEFAULT_PIN_SIZE_PERCENT);
    const [color, setColor] = React.useState(DEFAULT_PIN_COLOR);
    const [colorAnchor, setColorAnchor] = React.useState<HTMLElement | null>(null);
    const pin = usePinPosition(sizePercent / 100);

    function handleCreate() {
        onCreate({ x: pin.fraction.x, y: pin.fraction.y, size: sizePercent / 100, color });
    }

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth={false}
            PaperProps={{
                sx: { width: 'auto', maxWidth: 'none', backgroundColor: '#dde3e3' }
            }}
            TransitionProps={{
                onEnter: function () {
                    pin.reset();
                    setSizePercent(DEFAULT_PIN_SIZE_PERCENT);
                    setColor(DEFAULT_PIN_COLOR);
                }
            }}
        >
            <DialogTitle>{Translate('image-processing/place-pin/modal/title')}</DialogTitle>
            <DialogContent>
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        backgroundColor: '#b8cec8',
                        color: '#1a2e2a',
                        '& .MuiAlert-icon': { color: '#3a6b5e' }
                    }}
                >
                    {Translate('image-processing/place-pin/modal/info')}
                </Alert>
                <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 2 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {Translate('image-processing/place-pin/modal/size')}
                        </Typography>
                        <Slider
                            size="small"
                            sx={{ width: '200px' }}
                            min={MIN_PIN_SIZE_PERCENT}
                            max={MAX_PIN_SIZE_PERCENT}
                            step={PIN_SIZE_STEP_PERCENT}
                            value={sizePercent}
                            onChange={function (_e, value) {
                                setSizePercent(Array.isArray(value) ? value[0] : value);
                            }}
                            disabled={isLoading}
                            valueLabelDisplay="auto"
                            valueLabelFormat={function (value) {
                                return `${value} %`;
                            }}
                            aria-label={Translate('image-processing/place-pin/modal/size')}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                            {Translate('image-processing/place-pin/modal/color')}
                        </Typography>
                        <Box
                            component="button"
                            type="button"
                            aria-label={Translate('image-processing/place-pin/modal/color')}
                            disabled={isLoading}
                            onClick={function (e: React.MouseEvent<HTMLElement>) {
                                setColorAnchor(e.currentTarget);
                            }}
                            sx={{
                                width: '32px',
                                height: '32px',
                                p: 0,
                                borderRadius: '4px',
                                border: '1px solid rgba(0, 0, 0, 0.3)',
                                backgroundColor: color,
                                cursor: 'pointer'
                            }}
                        />
                        <Popover
                            open={colorAnchor !== null}
                            anchorEl={colorAnchor}
                            onClose={function () {
                                setColorAnchor(null);
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        >
                            <SketchPicker
                                color={color}
                                disableAlpha
                                presetColors={PRESET_PIN_COLORS}
                                onChange={function (value) {
                                    setColor(value.hex);
                                }}
                            />
                        </Popover>
                    </Box>
                </Box>
                <PinImagePreview
                    src={src}
                    alt={alt}
                    imageSize={pin.imageSize}
                    pinSize={pin.pinSize}
                    pinTopLeft={pin.pinTopLeft}
                    color={color}
                    onImageLoad={pin.handleImageLoad}
                    onDragStop={pin.handleDragStop}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading} aria-busy={isLoading}>
                    {Translate('image-processing/place-pin/modal/cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    disabled={isLoading || pin.imageSize.width === 0}
                    aria-busy={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                >
                    {Translate('image-processing/place-pin/modal/create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type DataLayerProps = {
    open: boolean;
    onClose: () => void;
    src: string;
    alt: string;
    orderId: number;
    imageId: number;
    onCreated: () => void;
};

export default function DataLayer({
    open,
    onClose,
    src,
    alt,
    orderId,
    imageId,
    onCreated
}: DataLayerProps) {
    const [showSuccess, setShowSuccess] = React.useState(false);
    const [errorMessages, setErrorMessages] = React.useState<string[] | null>(null);

    const mutation = useCreatePinnedImageMutation({
        orderId,
        imageId,
        onCreated: function () {
            setShowSuccess(true);
            onCreated();
        },
        onFailed: setErrorMessages
    });

    return (
        <>
            <PlacePinDialog
                open={open}
                onClose={onClose}
                src={src}
                alt={alt}
                isLoading={mutation.isLoading}
                onCreate={mutation.mutate}
            />
            <SuccessSnackbar
                open={showSuccess}
                onClose={function () {
                    setShowSuccess(false);
                }}
                text={Translate('image-processing/notification/place-pin/success')}
            />
            <FailureSnackbar
                open={errorMessages !== null}
                onClose={function () {
                    setErrorMessages(null);
                }}
                text={
                    errorMessages !== null ? (
                        <Box
                            component="section"
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            {errorMessages.map((message) => (
                                <article key={message}>{message}</article>
                            ))}
                        </Box>
                    ) : null
                }
            />
        </>
    );
}
